import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PhygitalIcon, { PhygitalLogo } from "@/components/PhygitalIcon";
import {
  CheckCircle2,
  FileText,
  ClipboardList,
  Rocket,
  ArrowRight,
  Sparkles,
  Shield,
  Gift,
  Eye,
  Mail,
  Heart
} from "lucide-react";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 9.47.j - Onboarding step icons + colors stay client-side (visual config),
// but the title/description copy is sourced from `surface_welcome` Airtable slots.
const ONBOARDING_STEP_VISUALS = [
  { id: 1, icon: FileText,      color: "text-purple-600", bgColor: "bg-purple-100" },
  { id: 2, icon: ClipboardList, color: "text-blue-600",   bgColor: "bg-blue-100"   },
  { id: 3, icon: Rocket,        color: "text-green-600",  bgColor: "bg-green-100"  },
];

export default function PilotWelcomePage() {
  const { portalId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { slotMap: w } = useUiSurface("surface_welcome");

  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [envelopeOpened, setEnvelopeOpened] = useState(false);
  
  // Fetch portal and user data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/pilot/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          setPortalData(data.portal);
          setUserData(data.user);
        }
      } catch (err) {
        console.error("Failed to fetch portal data:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [portalId]);
  
  const handleEnvelopeClick = () => {
    setEnvelopeOpened(true);
    setTimeout(() => setShowEnvelope(false), 800);
  };
  
  const handleContinue = () => {
    navigate(`/pilot/nda/${portalId}`);
  };
  
  // Gradient presets
  const GRADIENT_PRESETS = {
    purple: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    pink: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    blue: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    dark: "linear-gradient(135deg, #232526 0%, #414345 100%)",
    gold: "linear-gradient(135deg, #f5af19 0%, #f12711 100%)",
    chrome: "linear-gradient(135deg, #c0c0c0 0%, #8e8e8e 50%, #d4d4d4 100%)"
  };
  
  // Get background style - supports image URL, gradient preset, or custom CSS
  const getBackgroundStyle = () => {
    // First priority: uploaded background image
    const bgImage = portalData?.envelope_background;
    if (bgImage && (bgImage.startsWith('http') || bgImage.startsWith('/'))) {
      return {
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    
    // Second priority: gradient preset or custom CSS
    const gradientKey = portalData?.bg_gradient || "purple";
    if (gradientKey === "custom" && portalData?.custom_bg_css) {
      return { background: portalData.custom_bg_css };
    }
    
    return { background: GRADIENT_PRESETS[gradientKey] || GRADIENT_PRESETS.purple };
  };
  
  // HTHF-style envelope animation with customizable background
  if (showEnvelope) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center font-sans overflow-hidden cursor-pointer"
        style={getBackgroundStyle()}
        onClick={handleEnvelopeClick}
      >
        <div className={`transition-all duration-1000 ease-out ${envelopeOpened ? 'scale-110 opacity-0' : 'scale-100 opacity-100'}`}>
          {/* Envelope Container */}
          <div className="relative">
            {/* White Envelope Card */}
            <div className="w-80 h-52 bg-white rounded-lg shadow-2xl relative overflow-hidden">
              {/* Envelope flap (top triangle) */}
              <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-gray-100 to-white" 
                   style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
              
              {/* Inner card visible through envelope */}
              <div className="absolute inset-4 top-8 bg-gray-50 rounded border border-gray-200" />
            </div>
            
            {/* Phygital Logo Sticker - Centered on envelope */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center shadow-lg overflow-hidden">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <PhygitalLogo className="w-16 h-16" />
                )}
              </div>
            </div>
          </div>
          
          {/* "You're Invited" text below envelope */}
          <div className="text-center mt-8">
            <p className="text-white/90 text-xs tracking-[0.4em] uppercase mb-3 drop-shadow-lg" data-testid="welcome-envelope-invite">
              {slotText(w, "envelope_invite_label", "YOU'RE INVITED")}
            </p>
            <p className="text-white/70 text-xs drop-shadow-lg" data-testid="welcome-envelope-hint">
              {slotText(w, "envelope_tap_hint", "Tap anywhere to continue")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">{slotText(w, "loading_label", "Loading...")}</p>
        </div>
      </div>
    );
  }

  const firstName = userData?.first_name || userData?.name?.split(" ")[0] || "there";
  const pilotType = portalData?.pilot_type || portalData?.category_type || "beauty";

  // Resolve Airtable copy with token interpolation. Tokens supported: {first_name}, {pilot_type}.
  const interpolate = (s) =>
    (s || "")
      .replace(/\{first_name\}/g, firstName)
      .replace(/\{pilot_type\}/g, pilotType);

  const heroTitle = interpolate(slotText(w, "hero_title", "Welcome, {first_name}!"));
  const heroSubtitleRaw = slotText(
    w,
    "hero_subtitle",
    "You've been selected to join the {pilot_type} pilot program",
  );
  const heroSubtitleParts = heroSubtitleRaw.split("{pilot_type}");

  const benefits = [
    { icon: Gift,   label: slotText(w, "benefit_1_label", "Early Access"),       color: "text-pink-500"  },
    { icon: Shield, label: slotText(w, "benefit_2_label", "Priority Support"),   color: "text-blue-500"  },
    { icon: Eye,    label: slotText(w, "benefit_3_label", "Shape the Product"),  color: "text-green-500" },
  ];

  const onboardingSteps = ONBOARDING_STEP_VISUALS.map((v) => ({
    ...v,
    title:       slotText(w, `step_${v.id}_title`,       ["Sign NDA", "Complete Setup", "Build Your Experience"][v.id - 1]),
    description: slotText(w, `step_${v.id}_description`, [
      "Review and sign our confidentiality agreement",
      "Share details about your business and workflow",
      "We will customize your pilot based on your needs",
    ][v.id - 1]),
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </a>
        </div>
      </header>

      {/* Main Content - Clean Card Style */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          
          {/* Welcome Card */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-purple-100">
            
            {/* Welcome Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 mb-4">
                <Sparkles className="w-8 h-8 text-purple-600" />
              </div>
              <h1 className="text-3xl font-bold mb-2" data-testid="welcome-hero-title">
                {heroTitle}
              </h1>
              <p className="text-gray-600" data-testid="welcome-hero-subtitle">
                {heroSubtitleParts[0]}
                <span className="font-semibold text-purple-600 capitalize">{pilotType}</span>
                {heroSubtitleParts[1] ?? ""}
              </p>
            </div>

            {/* Decorative Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-purple-200" />
              <PhygitalIcon className="w-6 h-6 text-purple-400" variant="light" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-purple-200" />
            </div>

            {/* What's Next */}
            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-gray-800 text-center" data-testid="welcome-journey-heading">
                {slotText(w, "journey_heading", "Your onboarding journey:")}
              </h3>
              
              <div className="space-y-3">
                {onboardingSteps.map((step, idx) => {
                  const Icon = step.icon;
                  return (
                    <div 
                      key={step.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      data-testid={`welcome-step-${step.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${step.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${step.color}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{step.title}</p>
                        <p className="text-xs text-gray-500">{step.description}</p>
                      </div>
                      <span className="text-xs text-gray-400 font-mono">Step {idx + 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {benefits.map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <div key={benefit.label} className="text-center p-3 rounded-lg bg-gray-50">
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${benefit.color}`} />
                    <p className="text-xs font-medium text-gray-600">{benefit.label}</p>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <Button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6 rounded-xl text-lg font-semibold shadow-lg"
              data-testid="continue-to-nda-btn"
            >
              {slotText(w, "cta_continue", "Let's Get Started")}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Footer Note */}
            <p className="text-center text-xs text-gray-400 mt-6" data-testid="welcome-footer-note">
              {slotText(w, "footer_note", "This will only take about 5 minutes")}
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-gray-400">
        <p>© 2026 Phygital Systems. All rights reserved.</p>
      </footer>
    </div>
  );
}
